import _ from 'lodash'
import { TeamActions } from './teams'

import { BaseAction } from './index'
import { COUNTRY_ENUM } from 'services/api/constants'

export type BrandState = {
    uuid: string
    name: string
    description: string
    logo_image: {
        public_id: string
        image: string
        uuid: string
        img_main: string
    }
    team_name?: string
    country: COUNTRY_ENUM
    industry: string
    industry_others: string
    member_range: string
    team?: {
        uuid: string
        name: string
        type: string
        is_ai_caption_review_enabled: boolean
    }
}

export type BrandEntitiesState = {
    [uuid: string]: BrandState
}

export type BrandsState = {
    brands: string[]
    byId: BrandEntitiesState
}

const initialState: BrandsState = {
    byId: {},
    brands: [],
}

export enum BrandsActions {
    BRAND_LIST_REQUEST = 'brands/BRAND_LIST_REQUEST',
    SAVE_LIST = 'brands/SAVE_LIST',
    SAVE_BRANDS_BY_ID = 'brands/SAVE_BRANDS_BY_ID',
    BRAND_LIST_CLEAR = 'brands/BRAND_LIST_CLEAR',
    GET_BRAND = 'brands/GET_BRAND',
    CREATE_BRAND_REQUEST = 'brands/CREATE_BRAND_REQUEST',
    UPDATE_BRAND_REQUEST = 'brands/UPDATE_BRAND_REQUEST',
    SAVE_BRAND = 'brands/SAVE_BRAND',
    BRAND_LIST_CANCEL = 'BRAND_LIST_CANCEL',
}

export interface BrandRequestPayload {
    uuid: string
}

export interface BrandListRequestPayload {
    country?: string
    keyword?: string
    clear?: boolean
    onSuccessCallback?: () => void
    onErrorCallback?: () => void
}

interface SaveBrandListPayload {
    data: BrandState[] | undefined
    clear?: boolean
}

export const saveBrandList = (payload: SaveBrandListPayload) => ({
    payload,
    type: BrandsActions.SAVE_LIST,
})

export interface CreateBrandRequestPayload {
    name: string
    description: string
    country: string
    industry: string
    industry_others: string | null
    member_range: string
    logo_image: {
        uuid: string
        image: string
        img_main: string
    }
}

export const getBrand = (payload: BrandRequestPayload) => ({
    payload,
    type: BrandsActions.GET_BRAND,
})

export const brandListRequest = (payload: BrandListRequestPayload) => ({
    payload,
    type: BrandsActions.BRAND_LIST_REQUEST,
})

export const createBrandRequest = (payload: CreateBrandRequestPayload, onSuccess?: () => void) => ({
    payload,
    onSuccess,
    type: BrandsActions.CREATE_BRAND_REQUEST,
})

export interface UpdateBrandRequestPayload extends Omit<CreateBrandRequestPayload, 'country'> {
    uuid: string
}

export const updateBrandRequest = (payload: UpdateBrandRequestPayload, onSuccess?: () => void) => ({
    payload,
    onSuccess,
    type: BrandsActions.UPDATE_BRAND_REQUEST,
})

export type SaveBrandPayload = BrandState

export const saveBrand = (payload: SaveBrandPayload) => ({
    payload,
    type: BrandsActions.SAVE_BRAND,
})

export const clearBrand = () => ({
    type: BrandsActions.BRAND_LIST_CLEAR,
})

export const brandsReducer = (state: BrandsState = initialState, action: BaseAction) => {
    switch (action.type) {
        case BrandsActions.SAVE_LIST: {
            const { data, clear } = action.payload
            const safeData = data || []
            const uuids = safeData.map((brand: BrandState) => brand.uuid)

            if (clear) {
                return Object.assign({}, state, {
                    brands: uuids,
                    byId: safeData.reduce(
                        (memo: BrandEntitiesState, brand: BrandState) =>
                            Object.assign({}, memo, {
                                [brand.uuid]: brand,
                            }),
                        {},
                    ),
                })
            }

            return Object.assign({}, state, {
                brands: _.uniq([...state.brands, ...uuids]),
                byId: safeData.reduce(
                    (memo: BrandEntitiesState, brand: BrandState) =>
                        Object.assign({}, memo, {
                            [brand.uuid]: brand,
                        }),
                    state.byId,
                ),
            })
        }
        case TeamActions.SAVE_TEAM_BRAND_LIST: {
            const { data } = action.payload
            return Object.assign({}, state, {
                byId: data.reduce(
                    (memo: BrandEntitiesState, brand: BrandState) =>
                        Object.assign({}, memo, {
                            [brand.uuid]: brand,
                        }),
                    state.byId,
                ),
            })
        }
        case BrandsActions.BRAND_LIST_CLEAR: {
            return Object.assign({}, state, {
                byId: initialState.byId,
                brands: initialState.brands,
            })
        }
        case BrandsActions.SAVE_BRAND: {
            const { uuid } = action.payload
            return Object.assign({}, state, {
                brands: _.uniq([...state.brands, uuid]),
                byId: {
                    ...state.byId,
                    [uuid]: action.payload,
                },
            })
        }
        default:
            return state
    }
}

export const getList = (state: BrandsState) => {
    return state.brands.map((uuid: string) => {
        return state.byId[uuid]
    })
}

export const selectBrandsById = (state: BrandsState) => state.byId

export const selectBrandDetail = (state: BrandsState, uuid: string) => state.byId[uuid]
